import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Filter, Lock, CheckCircle } from 'lucide-react';
import { products, productCategories, keyBenefits } from '../data/mockData';
import { categorySEO } from '../data/seoData';
import SEO from '../components/SEO';

const Products = () => {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || 'All');

  useEffect(() => {
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  const filteredProducts =
    selectedCategory === 'All'
      ? products
      : products.filter((p) => p.category === selectedCategory);

  // Select category-specific SEO if a category is filtered; else use the generic Products SEO.
  const activeCategorySEO = categorySEO[selectedCategory];
  const seoTitle = activeCategorySEO
    ? activeCategorySEO.title
    : 'Industrial Products | Machine Guards, Dust Suppression & Wear Solutions';
  const seoDescription = activeCategorySEO
    ? activeCategorySEO.description
    : 'Explore Stallion Bulktech’s range of industrial products including conveyor guards, dust suppression systems, and wear-resistant solutions designed for harsh environments.';
  const seoPath = activeCategorySEO
    ? `/products?category=${encodeURIComponent(selectedCategory)}`
    : '/products';

  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={seoPath}
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
            <Lock className="h-4 w-4 text-cyan-600" />
            <p className="text-cyan-600 text-sm font-medium">Our Product Range</p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Industrial Machine
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient">
              Guards, Dust Suppression
            </span>
            <span className="block -mt-1 md:-mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient">
              & Wear Resistance
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            We deliver heavy-duty industrial machine guarding, dust suppression, and wear resistance solutions that protect equipment, control dust, and reduce wear in high-load environments, helping extend machine life and minimize unplanned downtime.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-20 z-40 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 overflow-x-auto pb-2">
            <Filter className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                selectedCategory === 'All'
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Products ({products.length})
            </button>
            {productCategories.map((category) => {
              const count = products.filter((p) => p.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <Link
                key={product.id}
                to={`/products/${product.slug}`}
                className="group relative flex"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                <div className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl h-full flex flex-col">
                  {/* Image with overlay */}
                  <div className="aspect-video overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50 relative flex-shrink-0">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Category badge */}
                    <div className="absolute top-4 left-4">
                      <div className="px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-cyan-200 rounded-full">
                        <span className="text-xs text-cyan-600 font-semibold">{product.category}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors line-clamp-2 min-h-[3.5rem]">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6 line-clamp-2 flex-grow">
                      {product.shortDescription}
                    </p>
                    
                    <div className="flex items-center text-cyan-600 font-semibold mt-auto">
                      <span className="text-sm">Learn More</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>

                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                </div>
              </Link>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Stallion Machine Guards, Dust Suppression & Wear Solutions</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Built for performance, durability, and real-world industrial conditions
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {keyBenefits.map((benefit, index) => (
              <div
                key={benefit}
                className="group bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-5 hover:border-cyan-300 transition-all duration-300 hover:shadow-xl flex items-start gap-3"
                data-testid={`key-benefit-${index}`}
              >
                <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                <p className="text-gray-800 text-sm sm:text-base font-medium leading-snug">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
